import { Button, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { BaseModal } from '@web/common-ui';
import { canSetNachversandEligibility, ErrorCode } from '@web/common';
import { ServerError } from '../../../request';
import { useParticipantDataStore } from '../../../store';
import { EligibilityRadioGroup } from './EligibilityRadioGroup';

type Props = {
  opened: boolean;
  onClose: () => void;
  currentValue: boolean | null;
};

export const NachversandEligibilityModal = ({
  opened,
  onClose,
  currentValue,
}: Props) => {
  const participantDataStore = useParticipantDataStore();
  const { t } = useTranslation('nachversand');
  const { userId } = useParams();
  const [selectedEligible, setSelectedEligible] = useState<boolean | null>(
    currentValue,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setSelectedEligible(currentValue);
    }
  }, [opened, currentValue]);

  useEffect(() => {
    if (opened) {
      setErrorMessage(null);
    }
  }, [opened]);

  const changeIsAllowed =
    selectedEligible !== null &&
    canSetNachversandEligibility(currentValue, selectedEligible);
  const isDeactivation = currentValue === true && selectedEligible === false;

  const handleSave = async () => {
    if (!userId || selectedEligible === null || !changeIsAllowed) {
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);

    try {
      await participantDataStore.setNachversandEligibility(
        userId,
        selectedEligible,
      );
      onClose();
    } catch (error) {
      // modal stays open, the badge was not updated
      const isStaleStateConflict =
        error instanceof ServerError &&
        error.errorCode === ErrorCode.NachversandEligibilityChangeNotAllowed;

      if (isStaleStateConflict) {
        // the shown value no longer matches the server (e.g. a colleague changed it),
        // so retrying cannot succeed - reload instead
        setErrorMessage(t('Modal.Errors.StaleState'));
        participantDataStore.retrieveUserInformation(userId);
      } else {
        setErrorMessage(t('Modal.Errors.Generic'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <BaseModal
      opened={opened}
      onClose={onClose}
      title={t('EditTitle')}
      description={t('Modal.Description')}
      withCloseButton
      buttons={[
        <Button
          key="save"
          color={isDeactivation ? 'red' : undefined}
          disabled={!changeIsAllowed || isSaving}
          onClick={() => void handleSave()}
        >
          {isDeactivation ? t('Modal.Deactivate') : t('common:Buttons.Save')}
        </Button>,
        <Button key="cancel" variant="subtle" onClick={onClose}>
          {t('common:Buttons.Cancel')}
        </Button>,
      ]}
    >
      <EligibilityRadioGroup
        currentValue={currentValue}
        selected={selectedEligible}
        onSelect={setSelectedEligible}
      />
      {isDeactivation && (
        <Text size="sm" c="red" mt="10px">
          {t('Modal.DeactivationWarning')}
        </Text>
      )}
      {errorMessage && (
        <Text size="sm" c="red" mt="10px">
          {errorMessage}
        </Text>
      )}
    </BaseModal>
  );
};
